// @ts-ignore
import { formatDate } from "date-fns/format";
import { find, get, last } from "lodash";
import pluralize from "pluralize";
import { formatValue } from "./FormatUtils";
import { getFormatter } from "./Formatters";
import { TEMPLATE_KEY_CHARS, splitTemplateLabel, stripParentAlias, stripTemplateLabels } from "./TemplateSegments";
import { getIdentifierValue, getNodeResult, getOrdinal } from "./graph-utils/GraphMisc";
import type { GraphNode, ParsedRuleGraph } from "./graph-utils/GraphTypes";
import { BEGIN_TIME_TIMESTAMP, END_TIME_TIMESTAMP, NOT_VALID } from "./graph-utils/GraphConstants";

const KEY = TEMPLATE_KEY_CHARS;
const SINGLE_TEMPLATE_VARIABLE_REGEX = new RegExp(String.raw`^\{\{\s*(${KEY}+)\s*\}\}$`);
const TEMPLATE_EXPRESSION_REGEX = new RegExp(
  String.raw`\{\{\s*(${KEY}+?)(?:\s*\|\s*([\w|\s]+))?(?:\s*((?:==|!=|>|<|>=|<=))\s+['"]?([\s\S]+?)['"]?\s*\?\s*(.+)\s*:\s*(.+))?\s*\}\}`,
  "g",
);
const STRIP_WRAPPING_QUOTES_REGEX = /^['"]|['"]$/g;
const TEMPLATE_PART_FORMATTER_REGEX = new RegExp(String.raw`^(${KEY}+?)(?:\s*\|\s*([\w|\s]+))$`);
const SIMPLE_TEMPLATE_CONDITIONAL_REGEX = new RegExp(
  String.raw`\{\{\s*(${KEY}+?)(?:\s*\|\s*([\w|\s]+))?\s*\?\s*(.+)\s*:\s*(.+)\s*\}\}`,
  "g",
);
const TEXT_TEMPLATE_REGEX = /{{(.*?)}}/g;
const TEXT_TEMPLATE_TEST_REGEX = /{{(.*?)}}/;

/**
 * Process a template string and return the processed string.
 *
 * @param str - The template string to process (e.g. `{{name}}`)
 * @param values - The values to be used in the template string (e.g. `{name: "John"}`)
 * @returns The processed string (e.g. `"John"`)
 */
export const templateString = (str: string | null | undefined, values: Record<string, any> = {}) => {
  if (!str) return "";
  // `as "label"` is editor-only (see TemplateSegments), so it never reaches the expression regexes.
  str = stripTemplateLabels(str);

  // Check if the entire template is just a single variable
  const singleVarMatch = str.trim().match(SINGLE_TEMPLATE_VARIABLE_REGEX);
  if (singleVarMatch) {
    // If the entire template is just a single variable, return the value as-is without converting to string
    const key = singleVarMatch[1].trim();
    return get(values, key);
  }

  let outcome = str.replace(TEMPLATE_EXPRESSION_REGEX,
  (match, key, formatterChain, comparisonExpression, comparisonValue, truePart, falsePart) => {
    let value = get(values, key);
    // Apply formatters if present
    if (formatterChain) {
      const formatList = formatterChain.split('|').map((f: any) => f.trim());
      for (const formatter of formatList) {
        if (getFormatter(formatter)) {
          if (formatter === 'atShiftEnd') {
            if (!value || !value.temporal) {
              value = '';
              break;
            }
          }
        }
      }
    }
    // Handle comparison check and conditional logic
    if (comparisonExpression !== undefined) {
      const operator = comparisonExpression.trim();
      const compareValue = comparisonValue.trim().replace(STRIP_WRAPPING_QUOTES_REGEX, '');
      let isTrue = false;

      // Convert values to numbers for numeric comparisons
      const numValue = typeof value === 'number' ? value : parseFloat(value);
      const numCompareValue = parseFloat(compareValue);
      switch (operator) {
        case '==':
          isTrue = String(value) === compareValue;
          break;
        case '!=':
          isTrue = String(value) !== compareValue;
          break;
        case '>':
          isTrue = !isNaN(numValue) && !isNaN(numCompareValue) && numValue > numCompareValue;
          break;
        case '<':
          isTrue = !isNaN(numValue) && !isNaN(numCompareValue) && numValue < numCompareValue;
          break;
        case '>=':
          isTrue = !isNaN(numValue) && !isNaN(numCompareValue) && numValue >= numCompareValue;
          break;
        case '<=':
          isTrue = !isNaN(numValue) && !isNaN(numCompareValue) && numValue <= numCompareValue;
          break;
        default:
          isTrue = false;
      }

      const cleanTruePart = truePart.trim().replace(STRIP_WRAPPING_QUOTES_REGEX, '');
      const cleanFalsePart = falsePart.trim().replace(STRIP_WRAPPING_QUOTES_REGEX, '');

      // Process formatters on true/false parts
      let processedTruePart = cleanTruePart;
      let processedFalsePart = cleanFalsePart;

      // Check if truePart has formatters
      const truePartMatch = cleanTruePart.match(TEMPLATE_PART_FORMATTER_REGEX);
      if (truePartMatch) {
        const [, trueKey, trueFormatterChain] = truePartMatch;
        let trueValue = get(values, trueKey);
        if (trueFormatterChain) {
          const formatList = trueFormatterChain.split('|').map((f: any) => f.trim());
          for (const formatter of formatList) {
            if (getFormatter(formatter)) {
              if (formatter === 'atShiftEnd') {
                if (!trueValue || !trueValue.temporal) {
                  trueValue = '';
                  break;
                }
              }
            }
          }
          processedTruePart = trueValue !== undefined ? trueValue : cleanTruePart;
        } else {
          // Check if it's a variable reference
          const trueValue = get(values, cleanTruePart);
          processedTruePart = trueValue !== undefined ? trueValue : cleanTruePart;
        }
      }

      // Check if falsePart has formatters
      const falsePartMatch = cleanFalsePart.match(TEMPLATE_PART_FORMATTER_REGEX);
      if (falsePartMatch) {
        const [, falseKey, falseFormatterChain] = falsePartMatch;
        let falseValue = get(values, falseKey);
        if (falseFormatterChain) {
          const formatList = falseFormatterChain.split('|').map((f: any) => f.trim());
          for (const formatter of formatList) {
            if (getFormatter(formatter)) {
              if (formatter === 'atShiftEnd') {
                if (!falseValue || !falseValue.temporal) {
                  falseValue = '';
                  break;
                }
              }
            }
          }
          processedFalsePart = falseValue !== undefined ? falseValue : cleanFalsePart;
        } else {
          // Check if it's a variable reference
          const falseValue = get(values, cleanFalsePart);
          processedFalsePart = falseValue !== undefined ? falseValue : cleanFalsePart;
        }
      }

      return isTrue ? processedTruePart : processedFalsePart;
    }

    // Handle basic truthy/falsey logic if no comparison expression
    if (truePart !== undefined && falsePart !== undefined) {
      const cleanTruePart = truePart.trim().replace(STRIP_WRAPPING_QUOTES_REGEX, '');
      const cleanFalsePart = falsePart.trim().replace(STRIP_WRAPPING_QUOTES_REGEX, '');
      return value ? cleanTruePart : cleanFalsePart;
    }

      // If it's an object, preserve its structure
      if (typeof value === "object" && value !== null) {
        return value;
      }
      return value !== undefined ? value : "";
    },
  );

  // Handle truthy/falsy conditionals without comparison operators
  outcome = outcome.replace(SIMPLE_TEMPLATE_CONDITIONAL_REGEX,
  (match, key, formatterChain, truePart, falsePart) => {
    let value = get(values, key);
    // Apply formatters if present
    if (formatterChain) {
      const formatList = formatterChain.split('|').map((f: any) => f.trim());
      for (const formatter of formatList) {
        if (getFormatter(formatter)) {
          if (formatter === 'atShiftEnd') {
            if (!value || !value.temporal) {
              value = '';
              break;
            }
          }
        }
      }
    }

    // Handle truthy/falsy logic
    const cleanTruePart = truePart.trim().replace(STRIP_WRAPPING_QUOTES_REGEX, '');
    const cleanFalsePart = falsePart.trim().replace(STRIP_WRAPPING_QUOTES_REGEX, '');

      // If the true/false parts are variable references, evaluate them
      const evaluatedTruePart = get(values, cleanTruePart) !== undefined ? get(values, cleanTruePart) : cleanTruePart;
      const evaluatedFalsePart =
        get(values, cleanFalsePart) !== undefined ? get(values, cleanFalsePart) : cleanFalsePart;

      return value ? evaluatedTruePart : evaluatedFalsePart;
    },
  );

  return outcome;
};

/**
 * Recursively processes an object of arbitrary depth, applying templateString to any string values.
 *
 * @param obj - The object to process
 * @param values - Values to be used in the templateString function
 * @returns A new object with all string values processed through templateString
 */
export const processTemplate = (obj: any, values: Record<string, any> = {}): any => {
  // Return directly if null or undefined
  if (obj === null || obj === undefined) {
    return obj;
  }

  // Process string directly
  if (typeof obj === "string") {
    return templateString(obj, values);
  }

  // Process arrays recursively
  if (Array.isArray(obj)) {
    return obj.map((item) => processTemplate(item, values));
  }

  if (typeof obj === "object" && !Array.isArray(obj)) {
    if (obj.$iterate && obj.$template) {
      const arr = get(values, obj.$iterate) || [];
      return arr.map((item: any, index: number) => {
        // Create values object with both the item data and $index
        const itemValues = { ...item, $index: index };
        return processTemplate(obj.$template, itemValues);
      });
    }

    // Process regular objects
    const result: Record<string, any> = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        result[key] = processTemplate(obj[key], values);
      }
    }
    return result;
  }

  // Return other types unchanged
  return obj;
};

const DEFAULT_VALUE = "...";

const cleanOutcomeValue = (val: any) => {
  if (val === null || val === NOT_VALID) return "Uncertain";
  if (val === undefined) return "Unknown";
  return val;
};
const cleanOutcomeDate = (val: any) => {
  if (val === BEGIN_TIME_TIMESTAMP) return "beginning of time";
  if (val === END_TIME_TIMESTAMP) return "end of time";
  return formatDate(val, "yyyy-mm-dd");
};

const outcomeRender = (val: any, node: GraphNode, pass: any) => {
  if (val?.temporal) {
    if (val.temporal.ranges.length === 1) return val.temporal.ranges[0].v;
    const outcome: string[] = [];

    for (const r of val.temporal.ranges) {
      outcome.push(`${cleanOutcomeValue(r.v)} until ${cleanOutcomeDate(r.r.end)}`);
    }
    return outcome.join(". ");
  }
  if (val === null || typeof val === "undefined") return undefined;
  if (!Number.isNaN(val) && val.toString().length === 4) return val;

  return val;
};

export interface TextProcessResult {
  text: string;
  templateText: string;
  dynamicAttributes: Set<string>;
}

const generateTemplateString = (key: string, formatters?: string[]) => {
  return `{{${[key, ...(formatters || [])].join("|")}}}`;
};

export const getTextTemplateRegex = () => TEXT_TEMPLATE_TEST_REGEX;

const isTemplatedPath = (path: string) => path.split("/").includes("@id");

const extractPath = (entity: string, nodeId: string, meta: any): string => {
  if (entity === "global") return nodeId;
  if (meta.entity === "global") return nodeId;
  const parent = meta.parent_path || meta.parent;
  const parts = [...(parent?.split("/") ?? []), meta.entity, meta.id || meta.index].filter(Boolean);
  const path: string[] = [];
  while (parts.length > 0) {
    const partEntity = parts.shift();
    const partId = parts.shift();
    // @ts-ignore - GitHub actions is the only place TS complains about this. #ihateTS
    path.push(partEntity);
    // @ts-ignore - GitHub actions is the only place TS complains about this. #ihateTS
    path.push(partId);
    if (partEntity === entity) {
      return `${path.join("/")}/${nodeId}`;
    }
  }
  return nodeId;
};

/**
 * The node an explicit instance path (e.g. `employees/1/<attribute>`) names. The last segment may
 * be the attribute's id or its description, matching how the Rust engine resolves paths.
 */
const findPathNode = (graph: any, key: string): GraphNode | undefined => {
  const byPath = (path: string) =>
    graph.node?.(path) || find(graph._nodes, (node) => node?.path === path || node?.id === path);
  const exact = byPath(key);
  if (exact) return exact;

  const slash = key.lastIndexOf("/");
  const attr = key.slice(slash + 1).toLowerCase();
  const attribute = find(graph._nodes, (node) => node?.description?.toLowerCase() === attr);
  return attribute ? byPath(`${key.slice(0, slash)}/${attribute.id}`) : undefined;
};

const processVar = (key: string, meta: any, data: any, pass: any, dependencies: Set<string>, options: any = {}) => {
  // `as "label"` is editor-only (see TemplateSegments): drop it before reading the key and formatters.
  key = splitTemplateLabel(key).expression.trim();
  let formatters: string[] = [];
  if (key.includes("|")) {
    formatters = key.split("|").map((f) => f.trim());
    key = formatters[0];
    formatters.shift();
  }
  // In some circumstances the backend needs to enforce (or remove) formats. Override this here
  if (options.forceFormatters) formatters = options.forceFormatters;
  key = stripParentAlias(key);

  // Lets `min` / `max` / `atShiftEnd` read another key the way this template would, unformatted.
  const resolve = (other: string) =>
    processVar(other, meta, data, pass, dependencies, { ignoreMissing: true, raw: true }).result;

  if (key.startsWith("variables.")) {
    const variableKey = key.slice("variables.".length);
    const variables = pass?.release?.variables || [];
    const found = variables.find((variable: any) => variable?.key === variableKey);
    if (found) {
      let result: any = found.value;
      if (!options.raw) result = formatValue(result, { formatters, locale: pass.release?.locale, resolve });
      return { result, templated: generateTemplateString(key, formatters) };
    }
    if (options.ignoreMissing) {
      return { result: undefined, templated: undefined };
    }
    return { result: DEFAULT_VALUE, templated: generateTemplateString(key, formatters) };
  }
  const directNode = key.includes("/") && pass.graph ? findPathNode(pass.graph, key) : undefined;
  // We assume that's an attribute they are referring to
  let attr = key;
  if (key.includes("/")) attr = last(key.split("/")) || "";
  let hiddenNode: GraphNode | undefined;
  let templatedNode: GraphNode | undefined;

  let isNode =
    directNode ||
    (pass.graph
      ? find(pass.graph._nodes, (node) => {
          if (!node) return false;
          const isCorrectNode =
            node.id === attr || node.description?.toLowerCase() === attr.toLowerCase() || node.publicId === attr;
          if (!isCorrectNode) {
            return false;
          }
          if (node.hidden) {
            hiddenNode = node;
          }
          // A base node whose entity is on a template row (`@id`) in this context's chain: there is
          // no instance yet, but the reference is still in scope
          if (!node.path && node.entity !== "global" && isTemplatedPath(extractPath(node.entity, node.id, meta))) {
            templatedNode = node;
          }

          if (node.entity === "global") {
            return true;
          }

          const metaParent = meta.parent || meta.parent_path;
          if (node.entity === meta.entity && node.index === meta.id && node.parent_path === metaParent) {
            return true;
          }
          // An ancestor's attribute, read from the instance in this context's own chain (e.g. the
          // employee that contains this position), not the entity's first instance
          if (node.path && node.path === extractPath(node.entity, node.id, meta)) {
            return true;
          }
          if (node.entity !== meta.entity && metaParent && node.path) {
            return node.path.startsWith(metaParent);
          }

          return false;
        })
      : false);
  if (!isNode) {
    isNode = hiddenNode ?? templatedNode;
  }

  let result: any;
  let templated: string | undefined;
  let node: GraphNode | undefined;
  if (isNode) {
    // A graph node - render it. Its path in this context is its own instance path, or the matching
    // instance in the context's chain (templated when that instance is an `@id` template row)
    const scopedPath =
      isNode.path ?? (isNode.entity === "global" ? isNode.id : extractPath(isNode.entity, isNode.id, meta));
    node = isNode.path ? isNode : pass.graph.node(scopedPath);

    if (!node?.hidden) {
      dependencies.add(node?.path ?? node?.id ?? scopedPath);
    }

    if (!node) {
      if (options.ignoreMissing) {
        result = undefined;
        templated = undefined;
      } else {
        result = DEFAULT_VALUE;
        templated = generateTemplateString(scopedPath, formatters);
      }
    } else {
      result = outcomeRender(getNodeResult(node), node, pass);
      let path = node.path ?? node.id;
      // we are a hidden node, we must be in an entity control
      if (node.hidden) {
        const relationship = find(pass.release.relationships, { name: node.entity });
        const parentRelationship = find(pass.release.relationships, {
          target: relationship?.source,
          containment: true,
        });
        path = `${node.entity}/@id/${node.id}`;
        if (parentRelationship && meta.entity === parentRelationship.name) {
          if (meta.path) {
            path = `${meta.path}/${path}`;
          } else if (meta.id) {
            path = `${meta.entity}/${meta.id}/${path}`;
          }
          const parent = meta.parent ?? meta.parent_path;
          if (parent) {
            path = `${parent}/${path}`;
          }
        }
        dependencies.add(path);
        result = DEFAULT_VALUE;
      }
      templated = generateTemplateString(path, formatters);
    }
  } else if (key === "identifier") {
    // This is either the value of the identifier attribute
    // or the id if not present
    // Grab relationship info so we can find the identifier
    const rel = find(pass.release.relationships, { name: meta.entity });
    if (!rel) throw "Invalid entity";
    const node = find(pass.graph._nodes, { description: rel.textSingular, index: meta.id }); // TODO support sub entities

    let value = getNodeResult(node);
    if (!value) value = meta.id;
    result = value;
  } else if (key.includes("entity.")) {
    // Information about the entity
    const rel = find(pass.release.relationships, { name: meta.entity });
    //console.log('re', rel);
    //console.log('entity subst', meta)
    if (!rel) throw "Invalid entity";
    if (key === "entity.singular") return { result: rel.textSingular.replace("the ", "") };
    if (key === "entity.plural") return { result: pluralize(rel.textSingular.replace("the ", "")) };
    if (key === "entity.ordinal") {
      return {
        result: getOrdinal(
          {
            entity: meta.entity,
            index: meta.id,
          },
          pass,
        ),
      };
    }
    if (key === "entity.index") {
      return {
        result: getOrdinal(
          {
            entity: meta.entity,
            index: meta.id,
          },
          pass,
          true,
        ),
      };
    }
    return { result: "" };
  } else if (key.includes("parent.")) {
    //console.log('parent', meta)
    const parent_path = meta.parent_path.split("/");
    const parent_index = parent_path.pop();
    const parent_entity = parent_path.pop();
    const p_query: any = {
      entity: parent_entity,
      index: parent_index,
      id: parent_index,
    };
    if (parent_path.length > 0) p_query.parent_path = parent_path.join("/");
    const rel = find(pass.release.relationships, { name: parent_entity });
    if (key === "parent.ordinal") {
      return { result: getOrdinal(p_query, pass) };
    }
    if (key === "parent.index") {
      return { result: getOrdinal(p_query, pass, true) };
    }
    if (key === "parent.singular") {
      return { result: rel ? rel.textSingular.replace("the ", "") : DEFAULT_VALUE };
    }
    if (key === "parent.plural") {
      return { result: rel ? pluralize(rel.textSingular.replace("the ", "")) : DEFAULT_VALUE };
    }
    if (key === "parent.identifier") {
      return { result: getIdentifierValue(p_query, pass.graph) || getOrdinal(p_query, pass) };
    }
  } else if (key.includes("meta.")) {
    result = get(pass, key.replace("meta.", ""));
  } else {
    result = get(data, key);
  }
  if (result !== undefined && !options.raw) {
    result = formatValue(result, { formatters, type: node?.type, locale: pass.release?.locale, resolve });
  }
  if (result === undefined && node && !node.hidden) {
    dependencies.add(node.path ?? node.id);
    if (options.ignoreMissing) {
      // Return undefined to signal that this variable should be left as-is in the template
      result = undefined;
      templated = undefined;
    } else {
      result = DEFAULT_VALUE;
      templated = generateTemplateString(node.path ?? node.id, formatters);
    }
  }
  // Handle case where result is undefined and there's no node (simple data lookup failed)
  if (result === undefined && !node && options.ignoreMissing) {
    result = undefined;
    templated = undefined;
  } else if (result === undefined && !node) {
    result = DEFAULT_VALUE;
    templated = DEFAULT_VALUE;
  }

  return { result, templated };
};

/**
 * Process a template string and return the processed string with template variables replaced.
 *
 * @param template - The template string to process (e.g. "Hello {{name}}")
 * @param meta - Metadata context for template processing
 * @param pass - Pass object containing graph and other processing context
 * @param options - Processing options
 * @param options.ignoreMissing - If true, leave unresolved template variables as-is instead of replacing with "..."
 * @returns Processed text result with text, templateText, and dynamicAttributes
 */
type TemplatePass = {
  graph: ParsedRuleGraph;
  release?: {
    variables?: Array<{ key: string; value: any }>;
    relationships?: any[];
    locale?: string;
  };
};

export const processText = <S extends string | undefined>(
  template: S,
  meta: any,
  pass: TemplatePass,
  options?: any,
): S extends undefined ? S : TextProcessResult => {
  const data = { ...pass.graph, ...meta };
  if (typeof template === "number" || typeof template === "boolean") {
    // @ts-ignore
    const text = template.toString();
    return {
      text: text,
      templateText: text,
      dynamicAttributes: new Set(),
    } satisfies TextProcessResult as any;
  }

  if (template === undefined || template === null || typeof template !== "string") {
    return template as any;
  }
  const dynamicAttributes = new Set<string>();
  const matches = template.matchAll(TEXT_TEMPLATE_REGEX);

  let lastIndex = 0;
  let text = "";
  let templateText = "";
  for (const match of matches) {
    const [fullMatch, key] = match;
    if (typeof key !== "string") continue;

    const textBefore = template.slice(lastIndex, match.index);

    text += textBefore;
    templateText += textBefore;
    // @ts-ignore
    const { result, templated } = processVar(key.trim(), meta, data, pass, dynamicAttributes, options);

    if (result === undefined && options?.ignoreMissing) {
      // Leave the original template variable in the text when ignoreMissing is true
      text += fullMatch;
      templateText += fullMatch;
    } else {
      text += result;
      templateText += templated || result;
    }

    lastIndex = (match.index ?? 0) + fullMatch.length;
  }

  const textAfter = template.slice(lastIndex);
  text += textAfter;
  templateText += textAfter;

  return {
    text: text,
    templateText,
    dynamicAttributes,
  } satisfies TextProcessResult as any;
};
